import React, { useState } from "react";
import { Plus } from "lucide-react";
import { TEST_IDS } from "@/lib/testIds";

const pillars = [
  {
    n: "I",
    label: "Control",
    desc: "Greater access and flexibility with your capital — capital that stays available when life and opportunity move quickly.",
    expanded:
      "Most people park money in accounts they can't touch without a penalty, a tax hit, or a banker's approval. Control means building a structure where your capital is always working — and always accessible. When an opportunity appears or an emergency hits, you move. No permission required.",
  },
  {
    n: "II",
    label: "Clarity",
    desc: "Understand how money actually moves through your financial life — so every decision is made with the full picture in view.",
    expanded:
      "Clarity isn't about knowing your account balance. It's about understanding what your money is actually doing between the moment it arrives and the moment it leaves. Most people are reactive because they're operating without a real picture. We build the picture first — then the decisions get easier.",
  },
  {
    n: "III",
    label: "Intentionality",
    desc: "Build systems designed for long-term goals, not short-term reactions. Every dollar with a defined role.",
    expanded:
      "A dollar without a job is a dollar that disappears. Intentionality means every dollar that comes in has somewhere to go — not because you're rigid, but because you've decided in advance what matters. That's how you stop managing money by feel and start building by design.",
  },
];

export const Pillars = () => {
  const [openIdx, setOpenIdx] = useState(null);

  return (
    <section
      id="approach"
      data-testid={TEST_IDS.pillars.section}
      className="bg-nearblack text-paper py-28 md:py-40"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="max-w-3xl">
          <p className="eyebrow-light mb-7">The Approach</p>
          <h2
            className="font-serif text-paper leading-[1.06] text-balance"
            style={{ fontSize: "clamp(2rem, 3.8vw, 3.6rem)", fontWeight: 400 }}
          >
            A quiet framework for{" "}
            <em className="italic text-camellight">
              how your money works
            </em>
            .
          </h2>
          <p className="mt-7 text-paper/75 text-base md:text-lg leading-[1.75] font-light max-w-2xl">
            The work isn’t about products. It’s about building a structure
            that makes the rest of your financial life calmer, clearer, and
            more intentional.
          </p>
          <p className="mt-5 text-camellight/80 text-[11px] tracking-[0.22em] uppercase font-medium">
            Click each pillar to read more
          </p>
        </div>

        <div className="mt-20 md:mt-24 grid grid-cols-1 md:grid-cols-3 gap-px bg-paper/10">
          {pillars.map(({ n, label, desc, expanded }, idx) => {
            const isOpen = openIdx === idx;
            return (
              <button
                key={label}
                type="button"
                onClick={() => setOpenIdx(isOpen ? null : idx)}
                aria-expanded={isOpen}
                aria-controls={`pillar-panel-${label.toLowerCase()}`}
                data-testid={`pillar-${label.toLowerCase()}`}
                className={`text-left bg-nearblack p-10 md:p-12 group transition-colors cursor-pointer focus:outline-none focus-visible:ring-1 focus-visible:ring-camel ${
                  isOpen ? "bg-[#241F1A]" : "hover:bg-[#241F1A]"
                }`}
              >
                <div className="flex items-baseline justify-between mb-12">
                  <span className="font-serif italic text-camellight text-xl">
                    {n}.
                  </span>
                  <span className="block h-px bg-camel/40 flex-1 mx-6" />
                  <Plus
                    className={`w-4 h-4 text-camellight transition-transform duration-300 ${
                      isOpen ? "rotate-45" : "rotate-0"
                    }`}
                    strokeWidth={1.5}
                    aria-hidden="true"
                  />
                </div>
                <h3
                  className="font-serif text-paper leading-tight"
                  style={{ fontSize: "clamp(1.85rem, 2.6vw, 2.5rem)", fontWeight: 400 }}
                >
                  {label}
                </h3>
                <p className="mt-6 text-paper/70 leading-[1.8] text-[15px] font-light">
                  {desc}
                </p>

                {/* Expandable panel */}
                <div
                  id={`pillar-panel-${label.toLowerCase()}`}
                  data-testid={`pillar-panel-${label.toLowerCase()}`}
                  className={`grid transition-all duration-500 ease-out ${
                    isOpen
                      ? "grid-rows-[1fr] opacity-100 mt-7"
                      : "grid-rows-[0fr] opacity-0 mt-0"
                  }`}
                >
                  <div className="overflow-hidden">
                    <div className="pt-6 border-t border-camel/30">
                      <p className="text-camellight/95 leading-[1.85] text-[15px] font-light">
                        {expanded}
                      </p>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};
